import { IDrillDownFilterCategory } from '~/client/src/shared/components/DrillDownFilter/DrillDownFilter.types'
import type { IOptionItem } from '~/client/src/shared/components/OptionsList/types'
import { IResourceProperty } from '~/client/src/shared/components/ResourceCardList/types'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { buildResourceFieldCatalogFromProperties } from '~/client/src/shared/resources/catalog/buildResourceFieldCatalog'
import { IResourceFieldCatalog } from '~/client/src/shared/resources/catalog/types'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import { IResourceFieldProjector } from '~/client/src/shared/resources/projection/types'
import { compareLabelsAlphanumeric } from '~/client/src/shared/utils/compareGroupsByLabel'

import { getResourceDefinition } from '../definitions'
import { getResourceFieldLabel } from './buildResourceListProperties'

const getCatalog = (
  resourceTypeId: string,
  properties?: IResourceProperty[],
  catalog?: IResourceFieldCatalog,
): IResourceFieldCatalog =>
  catalog || buildResourceFieldCatalogFromProperties(resourceTypeId, properties)

export function getResourceSortingOptions(
  resourceTypeId: string,
  properties?: IResourceProperty[],
  catalog?: IResourceFieldCatalog,
): IOptionItem[] {
  const resolvedCatalog = getCatalog(resourceTypeId, properties, catalog)

  return resolvedCatalog.sortableFields
    .map(field => ({
      key: field.key,
      label: field.label,
      glyph: field.glyph,
    }))
    .sort((a, b) => compareLabelsAlphanumeric(a.label, b.label))
}

export function getResourceGroupingOptions(
  resourceTypeId: string,
  properties?: IResourceProperty[],
  catalog?: IResourceFieldCatalog,
): IOptionItem[] {
  const resolvedCatalog = getCatalog(resourceTypeId, properties, catalog)

  return resolvedCatalog.groupableFields
    .map(field => ({
      key: field.key,
      label: field.label,
      glyph: field.glyph,
    }))
    .sort((a, b) => compareLabelsAlphanumeric(a.label, b.label))
}

/**
 * Narrowed to the resource's picker groupings, with their glyphs, where it declares any.
 * A grouping the fields do not offer is left out.
 */
export function toPickerGroupingOptions(
  resourceTypeId: string,
  options: IOptionItem[],
): IOptionItem[] {
  const pickerGroupings =
    getResourceDefinition(resourceTypeId)?.ui.pickerGroupings
  if (!pickerGroupings) return options

  return pickerGroupings.flatMap(({ key, glyph }) => {
    const option = options.find(item => item.key === key)
    return option ? [{ ...option, glyph }] : []
  })
}

interface IStringItem {
  id: string
  label: string
  nodeLabel?: string
  parentCompoundId?: string
}

export interface IResourceFieldOption {
  value: string
  label: string
}

export function getUniqueValuesBatched(
  records: IResourceRecord[],
  keys: string[],
  fieldProjector: Pick<IResourceFieldProjector, 'resolve'>,
): Map<string, IResourceFieldOption[]> {
  const optionMaps = new Map<string, Map<string, IResourceFieldOption>>()
  const unassignedFlags = new Map<string, boolean>()

  for (const key of keys) {
    optionMaps.set(key, new Map())
    unassignedFlags.set(key, false)
  }

  for (const record of records) {
    for (const key of keys) {
      const value = fieldProjector.resolve(record, key)
      if (value.isEmpty) {
        unassignedFlags.set(key, true)
        continue
      }

      value.filterEntries.forEach(entry => {
        optionMaps
          .get(key)
          ?.set(entry.id, { value: entry.id, label: entry.label })
      })
    }
  }

  const result = new Map<string, IResourceFieldOption[]>()
  for (const key of keys) {
    const sorted = Array.from(optionMaps.get(key)?.values() ?? []).sort(
      (a, b) => compareLabelsAlphanumeric(a.label, b.label),
    )
    if (unassignedFlags.get(key)) {
      sorted.push({ value: '', label: Localization.translator.unassigned })
    }
    result.set(key, sorted)
  }
  return result
}

export function getResourceFilterKeys(
  resourceTypeId: string,
  properties?: IResourceProperty[],
  catalog?: IResourceFieldCatalog,
): string[] {
  const resolvedCatalog = getCatalog(resourceTypeId, properties, catalog)

  return resolvedCatalog.filterableFields.map(field => field.key)
}

export function getResourceFilterCategories(
  resourceTypeId: string,
  getValuesForKey: (key: string) => IResourceFieldOption[],
  properties?: IResourceProperty[],
  catalog?: IResourceFieldCatalog,
): IDrillDownFilterCategory<IStringItem>[] {
  const resolvedCatalog = getCatalog(resourceTypeId, properties, catalog)
  const filterKeys = getResourceFilterKeys(resourceTypeId, properties, catalog)

  return filterKeys
    .map(key => {
      const hierarchy = resolvedCatalog.getField(key)?.hierarchy

      const values = getValuesForKey(key)
      const items: IStringItem[] = values.map(value => {
        const parentValue = hierarchy?.getParentValue(value.value)
        return {
          id: `${key}:${value.value}`,
          label: value.label,
          nodeLabel: hierarchy?.getOwnLabel(value.value),
          parentCompoundId: parentValue ? `${key}:${parentValue}` : undefined,
        }
      })

      const base = {
        id: key,
        label: getResourceFieldLabel(
          resourceTypeId,
          key,
          undefined,
          properties,
        ),
        items,
        getItemId: (item: IStringItem) => item.id,
        getItemLabel: (item: IStringItem) => item.label,
      }

      if (!hierarchy) {
        return base
      }

      return {
        ...base,
        kind: 'tree' as const,
        getItemParentId: (item: IStringItem) => item.parentCompoundId ?? null,
        getItemNodeLabel: (item: IStringItem) => item.nodeLabel || item.label,
      }
    })
    .sort((a, b) => compareLabelsAlphanumeric(a.label, b.label))
}
